import OrderDetailsTable from './OrderDetailsTable';
import { Card, Col, Row } from 'react-bootstrap';
import OrderDetailsSummaryCard from './OrderDetailsSummaryCard';
import { Link, useParams } from 'react-router-dom';
import IMerchProductOrder from '../../../models/restful/IMerchProductOrder';
import React from 'react';
import { useQuery } from '@tanstack/react-query';

import OrderInfoItem from '../../../components/info-items/OrderInfoItem';
import { apiV2 } from '../../../api/ApiV2';
import Loader from '../../../components/common/Loader/Loader';

interface IOrderDetailsProps {
  order?: IMerchProductOrder;
  hideTitle?: boolean;
}

const OrderDetails: React.FunctionComponent<IOrderDetailsProps> = props => {
  const { name } = useParams();

  const {
    data: order,
    isLoading,
    error
  } = useQuery({
    queryKey: ['orders', name],
    queryFn: async () => {
      if (props.order) {
        return props.order;
      }

      const response = await apiV2.crud.read({
        dataObjectName: 'MerchProductOrder',
        id: `${name}`,
        field: 'Name',
        dataObjectParams: { _view_nested: true }
      });

      return response.item as IMerchProductOrder;
    },
    enabled: !props.order && !!name,
    initialData: props.order
  });

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div>
        Error loading order:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div>
      <div className="mb-9">
        {!props.hideTitle && (
          <>
            <h2 className="mb-1">
              Order <span>{order?.Name}</span>
            </h2>
            <hr />
          </>
        )}

        <Row>
          <Col xs={12} xl={8} xxl={9}>
            <h4 className="mb-3">Shipping details</h4>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: 'auto 1fr' }}
            >
              <OrderInfoItem icon="mail" label="Email" />
              <div>
                <Link
                  className="fs-12 text-800 hover-text-primary"
                  to={'mailto:' + order.ShipmentOrderItem?.Email}
                >
                  {order.ShipmentOrderItem?.Email}
                </Link>
              </div>
              <OrderInfoItem icon="phone" label="Phone" />
              <div>
                <Link
                  className="fs-12 text-800 hover-text-primary"
                  to={'tel:' + order.ShipmentOrderItem?.Phone}
                >
                  {order.ShipmentOrderItem?.Phone}
                </Link>
              </div>
              <OrderInfoItem icon="home" label="Address" />
              <div>
                <p className="text-800 mb-0 fs-12">
                  {order.ShipmentOrderItem?.FirstName}{' '}
                  {order.ShipmentOrderItem?.LastName}
                  {', '}
                  {order.ShipmentOrderItem?.Address}
                  {', '} {order.ShipmentOrderItem?.CountryName}
                </p>
              </div>
              <OrderInfoItem icon="delivery" label="Tracking ID" />
              <div>
                <p className="text-800 mb-0 fs-12">
                  {order.ShipmentOrderItem?.Parcels[0]?.TrackingID}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <OrderDetailsTable order={order} />
            </div>
          </Col>
          <Col xs={12} xl={4} xxl={3}>
            <OrderDetailsSummaryCard order={order} />
            <Card>
              <Card.Body>
                <Card.Title as="h3" className="mb-4">
                  Order Status
                </Card.Title>
                <h6 className="mb-2">Payment status</h6>
                <p>{order.PaymentStatus}</p>
                <h6 className="mb-2">Order status</h6>
                <p>{order.OrderStatus?.Title}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <hr />
      </div>
    </div>
  );
};

export default OrderDetails;
